/**
 * /honorarios/cotizaciones/:folio — Detalle de cotización.
 *
 * Fase 16b · Hotfix UI de desviación comercial DG:
 *   borrador + requiere_aprobacion_dg  → banner ámbar + botón "Solicitar aprobación DG"
 *   pendiente_dg                        → banner "Esperando DG" + botón "Aprobar desviación" sólo si rol direccion/admin_ti
 *   aprobada (post DG)                  → alert verde + botón "Enviar al cliente"
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";
import {
    PRODUCTO_LABEL, COT_ESTADOS, fmtMXNDec, fmtMXNDec2,
    diasVigencia, colorVigencia,
} from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
    DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../components/ui/tooltip";
import { AlertTriangle, FileDown, RefreshCw, CheckCircle2, Clock, Shield } from "lucide-react";
import { toast } from "sonner";

export default function CotizacionDetallePage() {
    const { folio } = useParams();
    const { hasRole } = useAuth();
    const [cot, setCot] = useState(null);
    const [motivo, setMotivo] = useState("");
    const [rechazando, setRechazando] = useState(false);
    const [dgDialogOpen, setDgDialogOpen] = useState(false);

    const reload = () => api.get(`/honorarios/cotizaciones/${folio}`).then((r) => setCot(r.data));
    useEffect(() => { reload(); }, [folio]);

    if (!cot) return <div className="p-6 text-muted-foreground">Cargando…</div>;

    const estadoCfg = COT_ESTADOS[cot.estado] || { label: cot.estado, cls: "" };
    const r = cot.resultado || {};
    const u = r.unica_vez || {};
    const rec = r.recurrente_anual || {};
    const av = r.aviso_desviacion || {};
    const dias = diasVigencia(cot.vence_at);

    // Alias normalizado del campo aprobada_por (acepta nombre histórico)
    const aprobadaPor = cot.aprobada_por || cot.aprobada_por_dg || null;
    const puedeAprobarDG = hasRole([ROLES.DIRECCION, ROLES.ADMIN_TI]);
    const requiereDG = cot.requiere_aprobacion_dg && !aprobadaPor;

    const solicitarDG = async () => {
        try {
            await api.post(`/honorarios/cotizaciones/${cot.id}/solicitar-aprobacion-dg`);
            toast.success("Solicitud de aprobación enviada a Dirección General");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const aprobarDG = async () => {
        try {
            await api.post(`/honorarios/cotizaciones/${cot.id}/aprobar-desviacion-dg`);
            toast.success("Desviación aprobada por Dirección General");
            setDgDialogOpen(false);
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const enviarCliente = async () => {
        try {
            const res = await api.post(`/honorarios/cotizaciones/${cot.id}/enviar`);
            toast.success(`Cotización enviada · ${res.data.folio}`);
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const marcarAceptada = async () => {
        try {
            await api.post(`/honorarios/cotizaciones/${cot.id}/marcar-aceptada-cliente`);
            toast.success("Marcada como aceptada por el cliente");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const rechazar = async () => {
        if (motivo.length < 5) { toast.error("Motivo mínimo 5 caracteres"); return; }
        setRechazando(true);
        try {
            await api.post(`/honorarios/cotizaciones/${cot.id}/rechazar`, { motivo });
            toast.success("Rechazada");
            reload();
        } finally { setRechazando(false); }
    };
    const regen = async () => {
        try {
            await api.post(`/honorarios/cotizaciones/${cot.id}/regenerar-f06`);
            toast.success("F-06 PDF regenerado");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const descargar = () => window.open(
        `${api.defaults.baseURL}/honorarios/cotizaciones/${cot.id}/f06.pdf`, "_blank",
    );

    return (
        <TooltipProvider>
            <div className="space-y-6" data-testid="cotizacion-detalle">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                            {cot.folio_cotizacion || `Borrador ${cot.id.slice(0, 6)}`}
                        </h1>
                        <Badge className={estadoCfg.cls} data-testid="badge-estado">{estadoCfg.label}</Badge>
                        {/* Badge grande según estado + requiere_aprobacion_dg */}
                        {requiereDG && cot.estado === "borrador" && (
                            <Badge
                                data-testid="badge-borrador-con-desviacion"
                                className="bg-amber-100 text-amber-900 border-amber-400 border px-3 py-1 text-sm font-semibold"
                            >
                                <AlertTriangle className="h-3.5 w-3.5 mr-1 inline" />
                                Borrador con desviación — Pendiente aprobación DG
                            </Badge>
                        )}
                        {requiereDG && cot.estado === "pendiente_dg" && (
                            <Badge
                                data-testid="badge-esperando-dg"
                                className="bg-amber-100 text-amber-900 border-amber-400 border px-3 py-1 text-sm font-semibold"
                            >
                                <Clock className="h-3.5 w-3.5 mr-1 inline" />
                                Esperando aprobación DG
                            </Badge>
                        )}
                        {cot.vence_at && (
                            <Badge className={colorVigencia(dias)}>
                                Vigencia: {dias > 0 ? `${dias} días` : "Vencida"}
                            </Badge>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        {cot.producto} · {cot.producto_nombre || PRODUCTO_LABEL[cot.producto]}
                    </p>
                </div>

                {/* ======== Banner DG según estado ======== */}
                {requiereDG && cot.estado === "borrador" && (
                    <Alert
                        className="border-amber-400 bg-amber-50 text-amber-900"
                        data-testid="banner-borrador-dg"
                    >
                        <AlertTriangle className="h-4 w-4 text-amber-700" />
                        <AlertDescription className="flex items-center justify-between flex-wrap gap-3">
                            <span>
                                Esta cotización incluye una <b>desviación comercial por debajo del mínimo tarifario</b>.
                                Para enviarla al cliente se requiere aprobación de Dirección General.
                            </span>
                            <Button
                                size="sm"
                                onClick={solicitarDG}
                                data-testid="btn-solicitar-dg"
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                            >
                                Solicitar aprobación DG
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}
                {requiereDG && cot.estado === "pendiente_dg" && (
                    <Alert
                        className="border-amber-400 bg-amber-50 text-amber-900"
                        data-testid="banner-pendiente-dg"
                    >
                        <Clock className="h-4 w-4 text-amber-700" />
                        <AlertDescription className="flex items-center justify-between flex-wrap gap-3">
                            <span>
                                <b>Pendiente aprobación de Dirección General.</b>
                                {cot.solicitud_aprobacion_dg_por && (
                                    <> Solicitado por <code className="text-xs">{cot.solicitud_aprobacion_dg_por}</code>.</>
                                )}
                            </span>
                            {puedeAprobarDG ? (
                                <Dialog open={dgDialogOpen} onOpenChange={setDgDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button
                                            size="sm"
                                            data-testid="btn-abrir-aprobar-dg"
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            <Shield className="h-4 w-4 mr-1" />
                                            Aprobar desviación
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent data-testid="dialog-aprobar-dg">
                                        <DialogHeader>
                                            <DialogTitle>Aprobar desviación comercial</DialogTitle>
                                            <DialogDescription>
                                                Como Dirección General, confirmas que la propuesta
                                                por debajo del mínimo tarifario está autorizada.
                                                Esta acción queda registrada en la bitácora inmutable.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-3 text-sm">
                                            <div className="rounded-md border bg-muted/40 p-3 space-y-1">
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Mínimo tarifario</span>
                                                    <span className="font-medium">{fmtMXNDec2(av.monto_minimo_requerido)}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-muted-foreground">Propuesto</span>
                                                    <span className="font-semibold text-amber-800">{fmtMXNDec2(av.monto_propuesto)}</span>
                                                </div>
                                                <div className="flex justify-between border-t pt-1">
                                                    <span className="text-muted-foreground">Delta</span>
                                                    <span className="font-mono text-red-700">
                                                        −{fmtMXNDec2(av.delta_mxn)} ({av.delta_pct}%)
                                                    </span>
                                                </div>
                                            </div>
                                            {av.motivo_capturado && (
                                                <div>
                                                    <p className="text-xs uppercase text-muted-foreground mb-1">Motivo capturado</p>
                                                    <p className="rounded border bg-background p-2 text-sm italic">{av.motivo_capturado}</p>
                                                </div>
                                            )}
                                        </div>
                                        <DialogFooter>
                                            <Button variant="outline" onClick={() => setDgDialogOpen(false)}>
                                                Cancelar
                                            </Button>
                                            <Button
                                                onClick={aprobarDG}
                                                data-testid="btn-confirmar-aprobar-dg"
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                            >
                                                Aprobar desviación
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            ) : (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span>
                                            <Button
                                                size="sm"
                                                disabled
                                                data-testid="btn-aprobar-dg-disabled"
                                                className="opacity-60 cursor-not-allowed"
                                            >
                                                <Shield className="h-4 w-4 mr-1" />
                                                Aprobar desviación
                                            </Button>
                                        </span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Sólo Dirección General puede aprobar
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </AlertDescription>
                    </Alert>
                )}
                {aprobadaPor && cot.estado === "aprobada" && (
                    <Alert
                        className="border-emerald-300 bg-emerald-50 text-emerald-900"
                        data-testid="banner-aprobada-dg"
                    >
                        <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                        <AlertDescription className="flex items-center justify-between flex-wrap gap-3">
                            <span>
                                Desviación aprobada por <b>{aprobadaPor}</b>
                                {cot.aprobada_at && (
                                    <> el <code className="text-xs">{cot.aprobada_at.slice(0, 16).replace("T", " ")}</code></>
                                )}.
                                Lista para enviar al cliente.
                            </span>
                            <Button
                                size="sm"
                                onClick={enviarCliente}
                                data-testid="btn-enviar-cliente"
                                className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            >
                                Enviar al cliente
                            </Button>
                        </AlertDescription>
                    </Alert>
                )}

                {/* ======== Bloque Desviación Comercial (siempre que exista) ======== */}
                {av.activa && (
                    <Card data-testid="card-desviacion-comercial" className="border-amber-200">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-amber-600" />
                                Desviación comercial
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-sm">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div>
                                    <p className="text-xs uppercase text-muted-foreground">Mínimo tarifario</p>
                                    <p className="font-semibold">{fmtMXNDec2(av.monto_minimo_requerido)}</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase text-muted-foreground">Propuesto</p>
                                    <p className="font-semibold text-amber-800">{fmtMXNDec2(av.monto_propuesto)}</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase text-muted-foreground">Delta</p>
                                    <p className="font-mono text-red-700">
                                        −{fmtMXNDec2(av.delta_mxn)} ({av.delta_pct}%)
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase text-muted-foreground">Aprobada por</p>
                                    <p className="font-medium" data-testid="texto-aprobada-por">
                                        {aprobadaPor || "— pendiente —"}
                                    </p>
                                    {cot.aprobada_at && (
                                        <p className="text-xs text-muted-foreground">
                                            {cot.aprobada_at.slice(0, 16).replace("T", " ")}
                                        </p>
                                    )}
                                </div>
                            </div>
                            {av.motivo_capturado && (
                                <div className="mt-3">
                                    <p className="text-xs uppercase text-muted-foreground mb-1">Motivo</p>
                                    <p className="italic text-muted-foreground">{av.motivo_capturado}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* ======== Cards principales ======== */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Cliente</CardTitle></CardHeader>
                        <CardContent className="text-sm space-y-1">
                            <p className="font-semibold">{cot.cliente_prospecto?.razon_social}</p>
                            <p className="text-muted-foreground">RFC: {cot.cliente_prospecto?.rfc}</p>
                            <p>{cot.cliente_prospecto?.contacto_nombre}</p>
                            <p className="text-xs text-muted-foreground">{cot.cliente_prospecto?.contacto_email}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Parámetros</CardTitle></CardHeader>
                        <CardContent className="text-sm space-y-1">
                            <p>Patrimonio: {fmtMXNDec(cot.parametros_entrada?.patrimonio_mxn)}</p>
                            <p>Flujos anuales: {fmtMXNDec(cot.parametros_entrada?.flujos_anuales_mxn)}</p>
                            <p>Plazo: {cot.parametros_entrada?.plazo_anios} años</p>
                            <p>Slider: {(parseFloat(cot.parametros_entrada?.slider || 0) * 100).toFixed(0)}%</p>
                            <p>Actos/año: {cot.parametros_entrada?.actos_por_anio}</p>
                            <p>Sesiones/año: {cot.parametros_entrada?.sesiones_por_anio}</p>
                            <p>Convenios: {cot.parametros_entrada?.convenios_vida}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Acciones</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            {cot.folio_cotizacion && (
                                <Button className="w-full" variant="outline" onClick={descargar}
                                    data-testid="btn-descargar-f06-detalle">
                                    <FileDown className="h-4 w-4 mr-2" /> Descargar F-06 PDF
                                </Button>
                            )}
                            {cot.folio_cotizacion && cot.estado !== "borrador" && (
                                <Button className="w-full" variant="outline" onClick={regen}>
                                    <RefreshCw className="h-4 w-4 mr-2" /> Regenerar F-06
                                </Button>
                            )}
                            {(cot.estado === "enviada" || cot.estado === "aprobada") && !requiereDG && cot.folio_cotizacion && (
                                <Button className="w-full" onClick={marcarAceptada}
                                    data-testid="btn-marcar-aceptada">
                                    Marcar aceptada por cliente
                                </Button>
                            )}
                            {/* Enviar al cliente sólo cuando NO requiere DG o ya fue aprobada */}
                            {cot.estado === "borrador" && !requiereDG && (
                                <Button
                                    className="w-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                    onClick={enviarCliente}
                                    data-testid="btn-enviar-cliente-sin-dg"
                                >
                                    Enviar al cliente
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* ======== Desglose ======== */}
                <Card>
                    <CardHeader><CardTitle>Desglose</CardTitle></CardHeader>
                    <CardContent className="p-0">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs uppercase">
                                <tr>
                                    <th className="px-3 py-2 text-left">Concepto</th>
                                    <th className="px-3 py-2 text-right">Mínimo</th>
                                    <th className="px-3 py-2 text-right">Máximo</th>
                                    <th className="px-3 py-2 text-right">Propuesto</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ["Estudio", u.estudio_mxn],
                                    ["Aceptación", u.aceptacion_mxn],
                                    ["Admin anual", rec.administracion_mxn],
                                    ["Actos extraord./año", rec.actos_extraordinarios_mxn],
                                    ["Sesiones adicionales/año", rec.sesiones_adicionales_mxn],
                                    ["TOTAL AÑO 1 (sin IVA)", r.ano_1_sin_iva],
                                    ["IVA", r.iva_ano_1],
                                    ["TOTAL AÑO 1 (con IVA)", r.ano_1_con_iva],
                                    ["Recurrente anual año 2+", rec.total_mxn],
                                    ["TOTAL PLAZO (sin IVA)", r.total_plazo_sin_iva],
                                    ["TOTAL PLAZO (con IVA)", r.total_plazo_con_iva],
                                ].map(([lbl, v]) => v && (
                                    <tr key={lbl} className="border-t">
                                        <td className="px-3 py-2">{lbl}</td>
                                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmtMXNDec2(v.min)}</td>
                                        <td className="px-3 py-2 text-right text-xs text-muted-foreground">{fmtMXNDec2(v.max)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{fmtMXNDec2(v.propuesto)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>

                {cot.estado !== "rechazada" && cot.estado !== "aceptada_cliente" && (
                    <Card>
                        <CardHeader><CardTitle className="text-sm">Rechazar cotización</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            <Textarea placeholder="Motivo del rechazo (mínimo 5 caracteres)"
                                value={motivo} onChange={(e) => setMotivo(e.target.value)} />
                            <Button variant="destructive" disabled={motivo.length < 5 || rechazando}
                                onClick={rechazar}>Rechazar</Button>
                        </CardContent>
                    </Card>
                )}

                {cot.folio_cotizacion && (
                    <p className="text-xs text-muted-foreground">
                        Verificación pública:
                        <code className="ml-1 bg-muted px-1 py-0.5 rounded">
                            /api/public/verificar-cotizacion/{cot.folio_cotizacion}
                        </code>
                    </p>
                )}
            </div>
        </TooltipProvider>
    );
}
